import { useContext, useState } from 'react';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { store } from 'components/Store';

const useStakeholderNotesFunctions = () => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;

  const [userNotes, setUserNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { generalApiCall } = useGeneralApiCall();

  const getNotes = async ({ objectId, requestSource, contentType, pageNumber }) => {
    try {
      setIsLoading(true);
      const pathname = `/api/crm-activity/list?objectId=${objectId}&contentType=${contentType}`;
      const method = 'get';
      const results = await generalApiCall({
        pathname,
        method,
        needsAuthentication: true,
        requestSource,
        notShowErrorMessage: true,
      });
      if (results) {
        if (pageNumber) {
          setUserNotes([...userNotes, ...results]);
        } else {
          setUserNotes(results);
        }
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  const getNotesList = async ({
    requestSource,
    pageNumber,
    pageSize,
    userIds,
    contentType,
    dateTimeFromUtc,
    dateTimeToUtc,
    noteVisibility,
  }) => {
    try {
      const pathname = `/api/crm-activity/list?pageNumber=${pageNumber}&pageSize=${pageSize}${
        userIds?.length > 0 ? `&userIds=${userIds.join(',')}` : ''
      }${contentType ? `&contentType=${contentType}` : ''}${
        dateTimeFromUtc ? `&dateTimeFromUtc=${encodeURIComponent(dateTimeFromUtc)}` : ''
      }${dateTimeToUtc ? `&dateTimeToUtc=${encodeURIComponent(dateTimeToUtc)}` : ''}${
        noteVisibility !== 'excludeGeneralNotes' ? `&includeOrganisationWideActivities=true` : ''
      }`;
      const method = 'get';
      const results = await generalApiCall({
        pathname,
        method,
        needsAuthentication: true,
        requestSource,
        notShowErrorMessage: true,
      });
      if (results) {
        return results;
      }
    } catch (error) {}
  };

  const addNote = async ({ objectId, text, contentType, visibility }) => {
    try {
      const pathname = `/api/crm-activity/create`;
      const method = 'post';
      const requestProperties = {
        contentType: contentType ?? 'Stakeholder',
        objectId,
        text,
        activityType: 'Note',
      };
      if (visibility) {
        requestProperties.isOrganisationWide = visibility === 'agency';
      }
      const results = await generalApiCall({ pathname, requestProperties, method, needsAuthentication: true });
      if (results) {
        setUserNotes([results, ...userNotes]);
      }
    } catch (error) {}
  };

  const updateNote = async ({ note, text }) => {
    try {
      const pathname = `/api/crm-activity/update`;
      const method = 'post';
      const requestProperties = {
        ...note,
        text,
      };
      const results = await generalApiCall({ pathname, requestProperties, method, needsAuthentication: true });
      if (results) {
        let newUserNotes = [...userNotes];
        let noteItemPosition = newUserNotes.findIndex((item) => item?.id === note?.id);
        if (noteItemPosition !== undefined && noteItemPosition >= 0) {
          newUserNotes[noteItemPosition] = results;
        }
        setUserNotes(newUserNotes);
      }
    } catch (error) {}
  };

  const deleteNote = async ({ id }) => {
    try {
      const pathname = `/api/crm-activity/${id}`;
      const method = 'delete';
      const result = await generalApiCall({ pathname, method, needsAuthentication: true });
      if (result) {
        let newNotes = [...userNotes];
        let itemPosition = newNotes.findIndex((item) => item.id === id);
        if (itemPosition !== undefined) {
          newNotes.splice(itemPosition, 1);
          setUserNotes(newNotes);
        }
      }
    } catch (error) {}
  };

  const modifyNote = ({ note, text }) => {
    try {
      let newUserNotes = [...userNotes];
      let noteItemPosition = newUserNotes.findIndex((item) => item?.id === note?.id);
      if (noteItemPosition) {
        let newItem = { ...newUserNotes[noteItemPosition], text };
        newUserNotes[noteItemPosition] = newItem;
        setUserNotes(newUserNotes);
      }
    } catch (error) {}
  };

  const isNoteCreator = ({ note }) => {
    return note?.authorEmailAddress === team?.myself?.emailAddress;
  };

  return { userNotes, isLoading, getNotes, addNote, modifyNote, updateNote, isNoteCreator, deleteNote, getNotesList };
};

export default useStakeholderNotesFunctions;
